import type { ContentEntry, ContentKind, NewsTopic, ResourceCategory, SupportedLocale } from './types';
import { appConfig, resolveBackendAssetUrl } from '../../config/appConfig';

const API_BASE_URL = appConfig.api.baseUrl;

type ApiContentItem = {
  id: number;
  group_key: string;
  slug: string;
  kind: 'news' | 'resource';
  language: SupportedLocale;
  available_languages: SupportedLocale[];
  resource_category: ResourceCategory | null;
  news_topics: NewsTopic[];
  badge: string;
  title: string;
  summary: string;
  body: string[];
  cover_tone: ContentEntry['coverTone'];
  cover_image_url: string | null;
  published_at: string;
  is_pinned?: boolean;
};

type ApiListResponse = {
  results: ApiContentItem[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
};

type ApiDetailResponse = {
  item: ApiContentItem;
  translation_slugs?: Partial<Record<SupportedLocale, string>>;
};

export class ApiRequestError extends Error {
  status?: number;
  kind: 'network' | 'http';

  constructor(message: string, kind: 'network' | 'http', status?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.kind = kind;
    this.status = status;
  }
}

export type ContentHubListResult = {
  items: ContentEntry[];
  pagination: {
    page: number;
    totalPages: number;
    totalItems: number;
  };
};

export type ContentHubDetailResult = {
  item: ContentEntry;
  translationSlugs: Partial<Record<SupportedLocale, string>>;
};

const normalizeItem = (item: ApiContentItem): ContentEntry => ({
  id: String(item.id),
  groupKey: item.group_key,
  slug: item.slug,
  kind: item.kind === 'resource' ? 'resources' : 'news',
  language: item.language,
  availableLanguages: item.available_languages,
  title: item.title,
  summary: item.summary,
  body: item.body,
  badge: item.badge,
  date: item.published_at,
  isPinned: item.is_pinned ?? false,
  coverTone: item.cover_tone,
  coverImageUrl: resolveBackendAssetUrl(item.cover_image_url) ?? undefined,
  category: item.resource_category ?? undefined,
  newsTopics: item.news_topics,
});

const fetchJson = async <T>(path: string, params: Record<string, string | number | undefined>) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });

  const url = `${API_BASE_URL}${path}${query.size > 0 ? `?${query.toString()}` : ''}`;

  let response: Response;
  try {
    response = await fetch(url);
  } catch (error) {
    throw new ApiRequestError(error instanceof Error ? error.message : 'Backend is unavailable.', 'network');
  }

  if (!response.ok) {
    throw new ApiRequestError(`Request failed: ${response.status}`, 'http', response.status);
  }

  return response.json() as Promise<T>;
};

export const fetchContentList = async (
  kind: ContentKind,
  options: { page: number; pageSize: number; lang: SupportedLocale; topic?: NewsTopic; category?: ResourceCategory }
): Promise<ContentHubListResult> => {
  const path = kind === 'news' ? '/news/' : '/resources/';
  const data = await fetchJson<ApiListResponse>(path, {
    page: options.page,
    page_size: options.pageSize,
    lang: options.lang,
    topic: options.topic,
    category: options.category,
  });

  return {
    items: data.results.map(normalizeItem),
    pagination: {
      page: data.pagination.page,
      totalPages: data.pagination.total_pages,
      totalItems: data.pagination.total_items,
    },
  };
};

export const fetchContentDetail = async (kind: ContentKind, slug: string, lang: SupportedLocale): Promise<ContentHubDetailResult> => {
  const path = kind === 'news' ? `/news/${slug}/` : `/resources/${slug}/`;
  const data = await fetchJson<ApiDetailResponse>(path, { lang });
  return {
    item: normalizeItem(data.item),
    translationSlugs: data.translation_slugs ?? {},
  };
};

